import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Shield,
  Search,
  Users,
  UserCog,
  Layers,
  Lock,
  Globe,
  AlertTriangle,
  ChevronLeft,
  Save,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";
import { Spinner, Form } from "react-bootstrap";
import { RolePicker } from "../../access-control/RolePicker";
import { ACCESS_CONTROL_MANIFEST, ManifestPage } from "../../access-control/manifest";
import {
  AdminPageConfig,
  RoleOption,
  fetchAdminConfig,
  fetchRoles,
  saveAdminPageConfig,
} from "../../access-control/AccessControlService";
import { useAccessControl } from "../../access-control/AccessControlContext";

/* ── Types ── */

interface PageEntry {
  pageKey: string;
  label: string;
  manifest?: ManifestPage;
  config: AdminPageConfig;
  inManifest: boolean;
}

/* ── Helpers ── */

const emptyConfig = (): AdminPageConfig => ({
  viewRoleIds: [],
  adminRoleIds: [],
  elements: {},
});

const cloneConfig = (c: AdminPageConfig): AdminPageConfig => ({
  viewRoleIds: [...(c.viewRoleIds ?? [])],
  adminRoleIds: [...(c.adminRoleIds ?? [])],
  elements: Object.fromEntries(
    Object.entries(c.elements ?? {}).map(([k, v]) => [k, [...v]]),
  ),
});

const configsEqual = (a: AdminPageConfig, b: AdminPageConfig): boolean => {
  const eqArr = (x: number[] = [], y: number[] = []) => {
    if (x.length !== y.length) return false;
    const sx = [...x].sort();
    const sy = [...y].sort();
    return sx.every((v, i) => v === sy[i]);
  };
  if (!eqArr(a.viewRoleIds, b.viewRoleIds)) return false;
  if (!eqArr(a.adminRoleIds, b.adminRoleIds)) return false;
  const aKeys = Object.keys(a.elements ?? {});
  const bKeys = Object.keys(b.elements ?? {});
  const allKeys = Array.from(new Set(aKeys.concat(bKeys)));
  for (const k of allKeys) {
    if (!eqArr(a.elements?.[k], b.elements?.[k])) return false;
  }
  return true;
};

/* ── Page ── */

const ITeachAclConfig: React.FC = () => {
  const history = useHistory();
  const { refresh: refreshAcl } = useAccessControl();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successFlash, setSuccessFlash] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [serverConfig, setServerConfig] = useState<Record<string, AdminPageConfig>>({});
  const [draftConfig, setDraftConfig] = useState<Record<string, AdminPageConfig>>({});
  const [query, setQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([fetchAdminConfig(), fetchRoles()])
      .then(([cfg, rs]) => {
        if (cancelled) return;
        setRoles(rs);
        const pages = cfg.pages ?? {};
        setServerConfig(pages);
        const draft: Record<string, AdminPageConfig> = {};
        const allKeys = new Set<string>([
          ...ACCESS_CONTROL_MANIFEST.map((p) => p.pageKey),
          ...Object.keys(pages),
        ]);
        allKeys.forEach((k) => {
          draft[k] = pages[k] ? cloneConfig(pages[k]) : emptyConfig();
        });
        setDraftConfig(draft);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const entries: PageEntry[] = useMemo(() => {
    const manifestByKey = new Map<string, ManifestPage>(
      ACCESS_CONTROL_MANIFEST.map((p) => [p.pageKey, p]),
    );
    const manifestKeys = ACCESS_CONTROL_MANIFEST.map((p) => p.pageKey);
    const draftKeys = Object.keys(draftConfig);
    const seen: Record<string, true> = {};
    const ordered: string[] = [];
    manifestKeys.concat(draftKeys).forEach((k) => {
      if (!seen[k]) {
        seen[k] = true;
        ordered.push(k);
      }
    });
    return ordered.map((k) => ({
      pageKey: k,
      label: manifestByKey.get(k)?.label ?? k,
      manifest: manifestByKey.get(k),
      config: draftConfig[k] ?? emptyConfig(),
      inManifest: manifestByKey.has(k),
    }));
  }, [draftConfig]);

  const filteredEntries = useMemo(() => {
    if (!query.trim()) return entries;
    const q = query.toLowerCase();
    return entries.filter(
      (e) => e.label.toLowerCase().includes(q) || e.pageKey.toLowerCase().includes(q),
    );
  }, [entries, query]);

  // Auto-select first entry when list first loads
  useEffect(() => {
    if (!selectedKey && filteredEntries.length > 0) {
      setSelectedKey(filteredEntries[0].pageKey);
    }
  }, [filteredEntries, selectedKey]);

  const dirtyKeys = useMemo(() => {
    const keys: string[] = [];
    Object.keys(draftConfig).forEach((k) => {
      const server = serverConfig[k] ?? emptyConfig();
      if (!configsEqual(draftConfig[k], server)) keys.push(k);
    });
    return keys;
  }, [draftConfig, serverConfig]);

  const isDirty = dirtyKeys.length > 0;
  const selectedEntry = entries.find((e) => e.pageKey === selectedKey) ?? null;
  const selectedDirty = selectedKey ? dirtyKeys.includes(selectedKey) : false;

  const updateDraft = (pageKey: string, updater: (prev: AdminPageConfig) => AdminPageConfig) => {
    setDraftConfig((prev) => ({ ...prev, [pageKey]: updater(prev[pageKey] ?? emptyConfig()) }));
    setSuccessFlash(null);
  };

  const resetPage = (pageKey: string) => {
    setDraftConfig((prev) => ({
      ...prev,
      [pageKey]: serverConfig[pageKey] ? cloneConfig(serverConfig[pageKey]) : emptyConfig(),
    }));
  };

  const savePage = async (pageKey: string) => {
    setSaving(true);
    setError(null);
    try {
      const cfg = draftConfig[pageKey] ?? emptyConfig();
      await saveAdminPageConfig(pageKey, cfg);
      setServerConfig((prev) => ({ ...prev, [pageKey]: cloneConfig(cfg) }));
      await refreshAcl();
      setSuccessFlash(pageKey);
      setTimeout(() => setSuccessFlash((k) => (k === pageKey ? null : k)), 2200);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveAllDirty = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const k of dirtyKeys) {
        const cfg = draftConfig[k] ?? emptyConfig();
        await saveAdminPageConfig(k, cfg);
        setServerConfig((prev) => ({ ...prev, [k]: cloneConfig(cfg) }));
      }
      await refreshAcl();
      setSuccessFlash("__all__");
      setTimeout(() => setSuccessFlash((k) => (k === "__all__" ? null : k)), 2200);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        height: "calc(100vh - 60px)",
        overflow: "hidden",
        background: "var(--surface-page)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          background: "var(--surface-card)",
          borderBottom: "1px solid var(--border-default)",
          padding: "16px 28px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => history.push("/portlet-group/masters-setup")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 10px",
            background: "transparent",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            color: "var(--text-body)",
          }}
        >
          <ChevronLeft size={14} /> Back
        </button>
        <button
          onClick={() => history.push("/tenant-preferences")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 10px",
            background: "var(--brand-primary-subtle)",
            border: "1px solid var(--brand-primary-subtle)",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            color: "var(--brand-primary-hover)",
            fontWeight: 500,
          }}
          title="Tenant Preferences — branding, receipts, theme"
        >
          ⚙ Tenant Preferences
        </button>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "var(--gradient-brand)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-on-brand)",
          }}
        >
          <Shield size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "var(--text-strong)",
              letterSpacing: -0.2,
            }}
          >
            iTeach Page Access Control
          </h2>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {entries.length} pages · {roles.length} roles
            {isDirty && (
              <span
                style={{
                  marginLeft: 10,
                  padding: "1px 8px",
                  background: "var(--status-warning-bg)",
                  color: "var(--status-warning-text)",
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {dirtyKeys.length} unsaved
              </span>
            )}
          </div>
        </div>
        {successFlash === "__all__" && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--status-success-text)",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <CheckCircle2 size={16} /> All changes saved
          </div>
        )}
        <button
          onClick={saveAllDirty}
          disabled={!isDirty || saving}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            background: isDirty
              ? "var(--gradient-brand)"
              : "#e5e7eb",
            color: isDirty ? "#fff" : "#94a3b8",
            border: "none",
            borderRadius: 8,
            cursor: isDirty && !saving ? "pointer" : "not-allowed",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: isDirty ? "0 1px 2px rgba(79,70,229,0.25)" : "none",
          }}
        >
          {saving ? <Spinner animation="border" size="sm" /> : <Save size={14} />}
          Save all {isDirty ? `(${dirtyKeys.length})` : ""}
        </button>
      </div>

      {/* Body */}
      {loading && (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spinner animation="border" variant="primary" /> Loading access control…
        </div>
      )}
      {error && !loading && (
        <div
          style={{
            margin: 20,
            padding: 14,
            background: "var(--status-danger-bg)",
            color: "var(--status-danger)",
            border: "1px solid var(--status-danger-border)",
            borderRadius: 10,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* Sidebar */}
          <aside
            style={{
              width: 320,
              background: "var(--surface-card)",
              borderRight: "1px solid var(--border-default)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div style={{ padding: 14, borderBottom: "1px solid var(--border-subtle)" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 12,
                    transform: "translateY(-50%)",
                    color: "var(--text-faint)",
                  }}
                />
                <Form.Control
                  size="sm"
                  placeholder="Search pages…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ paddingLeft: 32, background: "var(--surface-page)", border: "1px solid var(--border-default)" }}
                />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filteredEntries.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>
                  No pages match "{query}"
                </div>
              )}
              {filteredEntries.map((entry) => {
                const active = entry.pageKey === selectedKey;
                const isPageDirty = dirtyKeys.includes(entry.pageKey);
                const hasRestrict = (entry.config.viewRoleIds?.length ?? 0) > 0;
                const elCount = entry.manifest?.elements.length ?? 0;
                const elRestrictedCount = Object.values(entry.config.elements ?? {}).filter(
                  (v) => v.length > 0,
                ).length;
                return (
                  <div
                    key={entry.pageKey}
                    onClick={() => setSelectedKey(entry.pageKey)}
                    style={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      borderLeft: active ? "3px solid #4f46e5" : "3px solid transparent",
                      background: active ? "#f5f3ff" : "transparent",
                      borderBottom: "1px solid var(--border-subtle)",
                      transition: "background 120ms",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) (e.currentTarget as HTMLDivElement).style.background = "var(--surface-page)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active) (e.currentTarget as HTMLDivElement).style.background = "transparent";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {hasRestrict ? (
                        <Lock size={12} color="var(--brand-primary)" />
                      ) : (
                        <Globe size={12} color="var(--text-faint)" />
                      )}
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-strong)",
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.label}
                      </div>
                      {isPageDirty && (
                        <span
                          title="Unsaved changes"
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "var(--status-warning)",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {!entry.inManifest && (
                        <span title="Not registered in manifest">
                          <AlertTriangle size={12} color="var(--status-warning)" />
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-faint)",
                        marginTop: 4,
                        marginLeft: 20,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entry.pageKey}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 6,
                        marginLeft: 20,
                        fontSize: 11,
                        color: "var(--text-muted)",
                      }}
                    >
                      <span>
                        <Users size={10} style={{ marginRight: 3 }} />
                        {entry.config.viewRoleIds?.length || 0}
                      </span>
                      <span>
                        <UserCog size={10} style={{ marginRight: 3 }} />
                        {entry.config.adminRoleIds?.length || 0}
                      </span>
                      <span>
                        <Layers size={10} style={{ marginRight: 3 }} />
                        {elRestrictedCount}/{elCount}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Detail */}
          <main style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
            {!selectedEntry && (
              <div style={{ padding: 60, textAlign: "center", color: "var(--text-faint)" }}>
                Select a page on the left
              </div>
            )}
            {selectedEntry && (
              <PageDetail
                entry={selectedEntry}
                roles={roles}
                isDirty={selectedDirty}
                saving={saving}
                savedFlash={successFlash === selectedEntry.pageKey}
                onUpdate={(updater) => updateDraft(selectedEntry.pageKey, updater)}
                onSave={() => savePage(selectedEntry.pageKey)}
                onReset={() => resetPage(selectedEntry.pageKey)}
              />
            )}
          </main>
        </div>
      )}
    </div>
  );
};

/* ── Detail panel ── */

interface PageDetailProps {
  entry: PageEntry;
  roles: RoleOption[];
  isDirty: boolean;
  saving: boolean;
  savedFlash: boolean;
  onUpdate: (updater: (prev: AdminPageConfig) => AdminPageConfig) => void;
  onSave: () => void;
  onReset: () => void;
}

const PageDetail: React.FC<PageDetailProps> = ({
  entry,
  roles,
  isDirty,
  saving,
  savedFlash,
  onUpdate,
  onSave,
  onReset,
}) => {
  const { config, manifest } = entry;

  const groups = useMemo(() => {
    if (!manifest) return [];
    const grouped: { label: string; items: typeof manifest.elements }[] = [];
    for (const el of manifest.elements) {
      const gLabel = el.group ?? "Other";
      const existing = grouped.find((g) => g.label === gLabel);
      if (existing) existing.items.push(el);
      else grouped.push({ label: gLabel, items: [el] });
    }
    return grouped;
  }, [manifest]);

  const updateView = (ids: number[]) =>
    onUpdate((prev) => ({ ...prev, viewRoleIds: ids }));
  const updateAdmin = (ids: number[]) =>
    onUpdate((prev) => ({ ...prev, adminRoleIds: ids }));
  const updateElement = (key: string, ids: number[]) =>
    onUpdate((prev) => ({
      ...prev,
      elements: { ...(prev.elements ?? {}), [key]: ids },
    }));

  return (
    <div style={{ padding: "28px 36px 120px", maxWidth: 1100 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 20,
          marginBottom: 28,
          paddingBottom: 20,
          borderBottom: "1px solid var(--border-default)",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: 0.8,
              marginBottom: 6,
            }}
          >
            Page · <code style={{ background: "var(--surface-sunken)", padding: "1px 6px", borderRadius: 4 }}>{entry.pageKey}</code>
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: "var(--text-strong)",
              letterSpacing: -0.4,
            }}
          >
            {entry.label}
          </h1>
          {!entry.inManifest && (
            <div
              style={{
                marginTop: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                background: "var(--status-warning-bg)",
                color: "var(--status-warning-text)",
                border: "1px solid var(--status-warning-border)",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <AlertTriangle size={14} />
              Not registered in <code>manifest.ts</code> — elements can't be configured here until added.
            </div>
          )}
        </div>
        {savedFlash && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--status-success-text)",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <CheckCircle2 size={16} /> Saved
          </div>
        )}
      </div>

      {/* Page-level access — two columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 28,
        }}
      >
        <AccessCard
          icon={<Users size={18} />}
          title="Who can open this page"
          subtitle="Leave empty to allow everyone. Root always has access."
          accent="#4f46e5"
        >
          <RolePicker
            roles={roles}
            selected={config.viewRoleIds ?? []}
            onChange={updateView}
            placeholder="Everyone (click to restrict)"
          />
        </AccessCard>
        <AccessCard
          icon={<UserCog size={18} />}
          title="Delegated admins"
          subtitle="Can edit this page's access settings. Root is always implicit."
          accent="#db2777"
        >
          <RolePicker
            roles={roles}
            selected={config.adminRoleIds ?? []}
            onChange={updateAdmin}
            placeholder="Only Root (click to delegate)"
            accent="#db2777"
          />
        </AccessCard>
      </div>

      {/* Elements */}
      {manifest && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <Layers size={18} color="var(--brand-primary)" />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-strong)" }}>
              Buttons & elements
            </h3>
            <span
              style={{
                fontSize: 11,
                background: "var(--brand-primary-subtle)",
                color: "var(--brand-primary-active)",
                padding: "2px 8px",
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              {manifest.elements.length}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {groups.map((grp) => (
              <div key={grp.label}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    margin: "0 0 10px 2px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {grp.label}
                  <span style={{ color: "var(--text-faint)", fontWeight: 500 }}>·</span>
                  <span style={{ color: "var(--text-faint)", fontWeight: 500 }}>
                    {grp.items.length} item{grp.items.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div
                  style={{
                    background: "var(--surface-card)",
                    border: "1px solid var(--border-default)",
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  {grp.items.map((el, i) => {
                    const selected = config.elements?.[el.key] ?? [];
                    const restricted = selected.length > 0;
                    return (
                      <div
                        key={el.key}
                        style={{
                          padding: "12px 16px",
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)",
                          gap: 16,
                          alignItems: "center",
                          borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                          background: restricted ? "#fafafa" : "#fff",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: "var(--text-strong)",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            {restricted ? (
                              <Lock size={12} color="var(--brand-primary)" />
                            ) : (
                              <Globe size={12} color="var(--text-faint)" />
                            )}
                            {el.label}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-faint)",
                              marginTop: 2,
                              marginLeft: 18,
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            }}
                          >
                            {el.key}
                          </div>
                        </div>
                        <RolePicker
                          roles={roles}
                          selected={selected}
                          onChange={(ids) => updateElement(el.key, ids)}
                          placeholder="Visible to all"
                          size="sm"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky action bar per-page */}
      {isDirty && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            marginLeft: 160, // offset for sidebar
            background: "var(--surface-inverse)",
            color: "var(--text-on-brand)",
            padding: "10px 14px",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(15,23,42,0.25)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            zIndex: 20,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            Unsaved changes on <strong>{entry.label}</strong>
          </span>
          <button
            onClick={onReset}
            disabled={saving}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "6px 12px",
              background: "transparent",
              color: "var(--text-faint)",
              border: "1px solid #334155",
              borderRadius: 8,
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <RotateCcw size={12} /> Reset
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "6px 14px",
              background: "var(--gradient-brand)",
              color: "var(--text-on-brand)",
              border: "none",
              borderRadius: 8,
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {saving ? <Spinner animation="border" size="sm" /> : <Save size={12} />}
            Save this page
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Reusable card for page-level access sections ── */

const AccessCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  accent: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, accent, children }) => (
  <div
    style={{
      background: "var(--surface-card)",
      border: "1px solid var(--border-default)",
      borderRadius: 12,
      padding: 18,
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 3,
        height: "100%",
        background: accent,
      }}
    />
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <div style={{ color: accent }}>{icon}</div>
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>{title}</h4>
    </div>
    <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>{subtitle}</p>
    {children}
  </div>
);

export default ITeachAclConfig;
